/**
 * Version-vs-version comparison as its own table (customers, their product lines, a totals row).
 *
 * Both versions are flattened to leaf values per period and summed by **customer name** and
 * **product line name**, nothing finer. The grid overlay this replaces matched leaves on their
 * full path (`class_of_trade/customer/product_line/sku`), so a version that filed a customer
 * under a class of trade, renamed a SKU or planned a SKU the other did not matched nothing, read
 * $0 on the compared side, and the footer summed only the rows that did match — the "numbers are
 * wrong, no totals" of item 6. Here every customer in either version gets a row, the side that
 * does not plan it shows 0, and the totals row is the sum of what is on screen.
 */
import { walk_version_leaf_periods, type VersionLeafValue, type VersionPathSegment } from "./version_compare"

/** Which number the compare table shows. */
export type CompareMeasure = "volume_dollars" | "cases_sold"

/** A customer or product line's value in both versions for one period or the year. */
export interface CompareCellPair {
  /** This version. */
  a: number
  /** The compared version. */
  b: number
}

export interface VersionCompareRow {
  key: string
  name: string
  periods: Record<string, CompareCellPair>
  total: CompareCellPair
  children?: VersionCompareRow[]
}

export interface VersionCompareTable {
  period_keys: string[]
  rows: VersionCompareRow[]
  total: VersionCompareRow
}

/** One leaf period of one version, named by the customer and product line it sits under. */
export interface CompareLeaf {
  customer: string
  product_line: string
  period: string
  volume_dollars: number
  cases_sold: number
}

const NO_PRODUCT_LINE = "(no product line)"

function segment_name(path: VersionPathSegment[], levels: string[]): string | undefined {
  for (const level of levels) {
    const hit = path.find((s: VersionPathSegment) => s.level === level && s.name)
    if (hit) return hit.name
  }
  return undefined
}

/**
 * Flatten a version (raw API nodes or the grid's tree) to compare leaves. The customer is the
 * `customer` level (else `retailer`, else the root), the product line the `product_line` level
 * (else the leaf's own name when the leaf is not a SKU).
 */
export function flatten_compare_leaves(nodes: unknown[], period_year_shift = 0): CompareLeaf[] {
  const out: CompareLeaf[] = []
  walk_version_leaf_periods(nodes, period_year_shift, (path: VersionPathSegment[], period: string, value: VersionLeafValue) => {
    const customer = segment_name(path, ["customer", "retailer"]) ?? path[0]?.name ?? "(unnamed)"
    const leaf = path[path.length - 1]
    const product_line =
      segment_name(path, ["product_line"]) ?? (leaf && leaf.level !== "sku" && leaf.name !== customer ? leaf.name : NO_PRODUCT_LINE)
    out.push({ customer, product_line, period, volume_dollars: value.volume_dollars, cases_sold: value.cases_sold })
  })
  return out
}

function name_key(name: string): string {
  return name.trim().toLowerCase()
}

function empty_row(key: string, name: string, period_keys: string[]): VersionCompareRow {
  const periods: Record<string, CompareCellPair> = {}
  for (const period of period_keys) periods[period] = { a: 0, b: 0 }
  return { key, name, periods, total: { a: 0, b: 0 } }
}

function add_into(row: VersionCompareRow, period: string, side: "a" | "b", amount: number): void {
  const cell = row.periods[period]
  if (!cell) return
  cell[side] = round2(cell[side] + amount)
  row.total[side] = round2(row.total[side] + amount)
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Build the compare table: rows per customer (sorted by this version's total, largest first,
 * then the compared version's), each with its product lines, over `period_keys` only. A customer
 * either version plans anywhere gets a row; leaves in periods outside the columns are left out of
 * every total, so the totals row is exactly the sum of the cells shown.
 */
export function build_version_compare_table(
  this_version: CompareLeaf[],
  compared_version: CompareLeaf[],
  period_keys: string[],
  measure: CompareMeasure
): VersionCompareTable {
  const shown = new Set(period_keys)
  const customers = new Map<string, VersionCompareRow>()
  const lines = new Map<string, Map<string, VersionCompareRow>>()
  const total = empty_row("total", "Total", period_keys)

  function ingest(leaves: CompareLeaf[], side: "a" | "b"): void {
    for (const leaf of leaves) {
      const amount = Number(leaf[measure] ?? 0)
      const customer_key = name_key(leaf.customer)
      let customer = customers.get(customer_key)
      if (!customer) {
        customer = empty_row(`customer:${customer_key}`, leaf.customer, period_keys)
        customers.set(customer_key, customer)
        lines.set(customer_key, new Map())
      }
      const line_key = name_key(leaf.product_line)
      const customer_lines = lines.get(customer_key)!
      let line = customer_lines.get(line_key)
      if (!line) {
        line = empty_row(`${customer.key}/line:${line_key}`, leaf.product_line, period_keys)
        customer_lines.set(line_key, line)
      }
      // The row exists for a customer either version plans anywhere; only the shown columns count.
      if (!shown.has(leaf.period)) continue
      add_into(customer, leaf.period, side, amount)
      add_into(line, leaf.period, side, amount)
      add_into(total, leaf.period, side, amount)
    }
  }
  ingest(this_version, "a")
  ingest(compared_version, "b")

  function by_size(x: VersionCompareRow, y: VersionCompareRow): number {
    return y.total.a - x.total.a || y.total.b - x.total.b || x.name.localeCompare(y.name)
  }

  const rows = [...customers.entries()]
    .map(([customer_key, customer]: [string, VersionCompareRow]) => ({
      ...customer,
      children: [...(lines.get(customer_key)?.values() ?? [])].sort(by_size),
    }))
    .sort(by_size)

  return { period_keys, rows, total }
}

/** Δ% of this version against the compared one; `null` when the compared side is 0. */
export function compare_delta_pct(pair: CompareCellPair): number | null {
  if (pair.b === 0) return null
  return ((pair.a - pair.b) / Math.abs(pair.b)) * 100
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function sku(name: string, rows: Array<[string, number, number]>, level = "sku") {
    return {
      name,
      level,
      children: [],
      sales_data: rows.map(([period, volume_dollars, cases_sold]) => ({ period, source_kind: "forecast", volume_dollars, cases_sold, store_count: 5 })),
    }
  }

  // This version files Kroger under a class of trade; the compared one does not, and it plans a
  // SKU (Lime) and a customer (Publix) this version lacks, in a month (March) this version skips.
  const this_version_nodes = [
    {
      name: "Grocery",
      level: "class_of_trade",
      children: [
        {
          name: "Kroger",
          level: "customer",
          children: [
            {
              name: "Sparkling",
              level: "product_line",
              children: [sku("Yuzu 12oz", [["2027-01", 1000, 100], ["2027-02", 1100, 110]])],
            },
          ],
        },
      ],
    },
  ]
  const compared_nodes = [
    {
      name: "Kroger",
      level: "customer",
      children: [
        {
          name: "Sparkling",
          level: "product_line",
          children: [
            sku("Yuzu 12oz", [["2027-01", 900, 90], ["2027-03", 950, 95]]),
            sku("Lime 12oz", [["2027-01", 400, 40]]),
          ],
        },
      ],
    },
    {
      name: "Publix",
      level: "customer",
      children: [{ name: "Still", level: "product_line", children: [sku("Plain", [["2027-02", 300, 30]])] }],
    },
  ]
  const period_keys = ["2027-01", "2027-02", "2027-03"]

  describe("flatten_compare_leaves", () => {
    it("names each leaf period by customer and product line whatever the levels around them", () => {
      const leaves = flatten_compare_leaves(this_version_nodes)
      expect(leaves).toEqual([
        { customer: "Kroger", product_line: "Sparkling", period: "2027-01", volume_dollars: 1000, cases_sold: 100 },
        { customer: "Kroger", product_line: "Sparkling", period: "2027-02", volume_dollars: 1100, cases_sold: 110 },
      ])
    })

    it("uses a non-SKU leaf's own name as its product line", () => {
      const leaves = flatten_compare_leaves([{ name: "Target", level: "customer", children: [sku("Cans", [["2027-01", 10, 1]], "product_line")] }])
      expect(leaves[0]).toMatchObject({ customer: "Target", product_line: "Cans" })
    })

    it("reads the grid's own tree (metrics + period objects) the same way", () => {
      const grid_tree = [
        {
          name: "Kroger",
          level: "customer",
          children: [
            {
              name: "Yuzu",
              level: "sku",
              children: [],
              sales_data: [
                { period: { period_key: "2027-01", year: 2027, month: 1 }, metrics: { volume_dollars: 12, cases_sold: 2, store_count: 1, case_cost: 6 }, is_forecast: true },
                // Untyped plan rows count with the forecast, as they do on the grid.
                { period: { period_key: "2027-01", year: 2027, month: 1 }, metrics: { volume_dollars: 3, cases_sold: 1, store_count: 1, case_cost: 3 }, is_forecast: true },
              ],
            },
          ],
        },
      ]
      expect(flatten_compare_leaves(grid_tree)).toEqual([
        { customer: "Kroger", product_line: NO_PRODUCT_LINE, period: "2027-01", volume_dollars: 15, cases_sold: 3 },
      ])
    })
  })

  describe("build_version_compare_table", () => {
    const table = build_version_compare_table(
      flatten_compare_leaves(this_version_nodes),
      flatten_compare_leaves(compared_nodes),
      period_keys,
      "volume_dollars"
    )

    it("matches a customer across versions even when one files it under a class of trade", () => {
      const kroger = table.rows.find((r: VersionCompareRow) => r.name === "Kroger")!
      expect(kroger.periods["2027-01"]).toEqual({ a: 1000, b: 1300 })
      expect(kroger.periods["2027-02"]).toEqual({ a: 1100, b: 0 })
      expect(kroger.periods["2027-03"]).toEqual({ a: 0, b: 950 })
      expect(kroger.total).toEqual({ a: 2100, b: 2250 })
    })

    it("keeps a customer only the compared version plans, with this side at 0", () => {
      const publix = table.rows.find((r: VersionCompareRow) => r.name === "Publix")!
      expect(publix.total).toEqual({ a: 0, b: 300 })
      expect(publix.periods["2027-02"]).toEqual({ a: 0, b: 300 })
      expect(publix.children?.[0]).toMatchObject({ name: "Still", total: { a: 0, b: 300 } })
    })

    it("totals are the sum of every leaf shown, on both sides", () => {
      const sum_a = table.rows.reduce((s: number, r: VersionCompareRow) => s + r.total.a, 0)
      const sum_b = table.rows.reduce((s: number, r: VersionCompareRow) => s + r.total.b, 0)
      expect(table.total.total).toEqual({ a: 2100, b: 2550 })
      expect(table.total.total.a).toBe(sum_a)
      expect(table.total.total.b).toBe(sum_b)
      expect(table.total.periods["2027-01"]).toEqual({ a: 1000, b: 1300 })
      // A customer's row is the sum of its product lines.
      const kroger = table.rows.find((r: VersionCompareRow) => r.name === "Kroger")!
      const lines_b = kroger.children!.reduce((s: number, line: VersionCompareRow) => s + line.total.b, 0)
      expect(lines_b).toBe(kroger.total.b)
    })

    it("counts only the columns on screen", () => {
      const jan_only = build_version_compare_table(
        flatten_compare_leaves(this_version_nodes),
        flatten_compare_leaves(compared_nodes),
        ["2027-01"],
        "cases_sold"
      )
      expect(jan_only.total.total).toEqual({ a: 100, b: 130 })
      expect(jan_only.rows.map((r: VersionCompareRow) => r.name)).toEqual(["Kroger", "Publix"])
    })

    it("orders customers largest first by this version, then the compared one", () => {
      expect(table.rows.map((r: VersionCompareRow) => r.name)).toEqual(["Kroger", "Publix"])
    })
  })

  describe("compare_delta_pct", () => {
    it("is this against compared, and undefined against zero", () => {
      expect(compare_delta_pct({ a: 110, b: 100 })).toBeCloseTo(10)
      expect(compare_delta_pct({ a: 0, b: 100 })).toBe(-100)
      expect(compare_delta_pct({ a: 100, b: 0 })).toBeNull()
    })
  })
}
